/*============================================================================
  CartDrawer
    Slide in side cart

    Requires:
      1. #CartDrawer-Overlay: full screen child overlay
      2. .drawer__close: button to close drawer
      3. #cart-icon-bubble: a element cart icon somewhere in document
      4. .drawer__inner: inner content of drawer
============================================================================*/
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.focusTrap = new FocusTrap();
    this.setupEventListeners();
    this.setupCartIcon();
  }

  setupEventListeners() {
    // ESC to close
    this.addEventListener('keyup', (e) => {
      if (e.key === 'Escape')
        this.close();
    });

    // Event delegation for clicks so behavior works even when re-rendered
    this.addEventListener('click', (e) => {
      // Overlay click
      if (e.target.id === 'CartDrawer-Overlay') {
        this.close();
      }

      // Close button click
      if (e.target.closest('.drawer__close')) {
        this.close();
      }
    });
  }

  // Since cart-icon-bubble is an 'a' element, need to change aria to button
  setupCartIcon() {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon) return;

    cartIcon.setAttribute('role', 'button');
    cartIcon.setAttribute('aria-haspopup', 'dialog');

    // This is where we override the a element to open drawer on click
    cartIcon.addEventListener('click', (e) => {
      e.preventDefault();
      this.open(cartIcon);
    });

    cartIcon.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        this.open(cartIcon);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      const container = this.querySelector('.drawer__inner');
      const focusElement = this.querySelector('.drawer__close');

      this.focusTrap.activate(container, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.classList.remove('animate');
    this.focusTrap.deactivate(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.getSectionsToRender().forEach((section) => {
      const element = document.querySelector(section.selector);
      if (!element) return;

      element.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: ".drawer__inner"
      },
      {
        id: 'header',
        selector: "#cart-icon-bubble"
      }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);